import { Suspense, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import MoroccanLoader from './MoroccanLoader'

/**
 * Suspense boundary for lazy route pages, placed at each shell's <Outlet/>.
 *
 * Without it, the first navigation to any page suspends up to the single
 * root boundary in App.tsx, which replaces the WHOLE app (sidebar, header,
 * everything) with the full-screen loader — a flash on every first visit.
 * A boundary per shell makes the page chunk suspend locally: the shell chrome
 * stays mounted and only the content area shows this compact fallback.
 * The root boundary remains as the safety net for the initial shell chunks.
 */
function RouteFallback() {
  const { t } = useTranslation()
  return (
    <div
      className="flex min-h-[40vh] items-center justify-center"
      role="status"
      aria-label={t('common.loading')}
      data-testid="route-fallback"
    >
      <MoroccanLoader size="sm" message={t('common.loading')} />
    </div>
  )
}

export default function RouteSuspense({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>
}
